import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Plus, Pencil, Trash2, Printer, FileSignature, Package, Upload, Download } from "lucide-react";
import { useAuth } from "@/AuthContext";
import { API } from "@/config";

export const InvoiceForm = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: "",
    sales_agent: "",
    payment_link: "",
    items: [],
    discount_type: "none",
    discount_value: 0,
    tax_rate: 0,
    payment_type: "full_payment",  // full_payment, financing, lease_to_own
    payment_terms: "Net 30",
    notes: "",
    payment_plan: {
      enabled: false,
      down_payment: 0,
      monthly_payment: 0,
      number_of_months: 0,
      apr: 0,
      total_financed: 0
    },
    lease_plan: {
      enabled: false,
      monthly_payment: 0,
      down_payment_months: 3,
      down_payment_amount: 0,
      total_months: 12,
      apr: 0,
      billing_start_date: null,
      lease_start_date: null,
      months_completed: 0,
      ownership_transferred: false
    },
    cc_authorization: {
      cardholder_name: "",
      card_number: "",
      expiration_date: "",
      cvv: "",
      billing_zip: "",
      id_number: "",
      authorization_date: ""
    }
  });
  const [currentItem, setCurrentItem] = useState({
    product_id: "",
    quantity: 1,
    price: 0
  });

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    if (isEdit && id) {
      fetchInvoice();
    }
  }, [id, isEdit]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error("Error fetching customers");
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error("Error fetching products");
    }
  };

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}`);
      setFormData({
        customer_id: response.data.customer_id,
        sales_agent: response.data.sales_agent || "",
        payment_link: response.data.payment_link || "",
        items: response.data.items,
        discount_type: response.data.discount_type || "none",
        discount_value: response.data.discount_value || 0,
        tax_rate: response.data.tax_rate,
        payment_type: response.data.payment_type || "full_payment",
        payment_terms: response.data.payment_terms,
        notes: response.data.notes,
        payment_plan: response.data.payment_plan,
        lease_plan: response.data.lease_plan || {
          enabled: false,
          monthly_payment: 0,
          down_payment_months: 3,
          down_payment_amount: 0,
          total_months: 12,
          apr: 0,
          billing_start_date: null,
          lease_start_date: null,
          months_completed: 0,
          ownership_transferred: false
        },
        cc_authorization: response.data.cc_authorization || {
          cardholder_name: "",
          card_number: "",
          expiration_date: "",
          cvv: "",
          billing_zip: "",
          id_number: "",
          authorization_date: ""
        }
      });
    } catch (error) {
      toast.error("Error fetching invoice");
    }
  };

  const addItem = () => {
    if (!currentItem.product_id) {
      toast.error("Please select a product");
      return;
    }

    const product = products.find(p => p.id === currentItem.product_id);
    const price = currentItem.price || product.price;
    const total = currentItem.quantity * price;

    const newItem = {
      product_id: product.id,
      product_name: product.name,
      description: product.description,
      quantity: currentItem.quantity,
      price: price,
      total: total
    };

    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });

    setCurrentItem({
      product_id: "",
      quantity: 1,
      price: 0
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculatePaymentPlan = () => {
    if (!formData.payment_plan.enabled) return;

    const subtotal = formData.items.reduce((sum, item) => sum + item.total, 0);
    
    // Calculate discount
    let discountAmount = 0;
    if (formData.discount_type === "percentage") {
      discountAmount = subtotal * (formData.discount_value / 100);
    } else if (formData.discount_type === "fixed") {
      discountAmount = formData.discount_value;
    }
    
    const subtotalAfterDiscount = subtotal - discountAmount;
    const tax = subtotalAfterDiscount * (formData.tax_rate / 100);
    const total = subtotalAfterDiscount + tax;
    
    const downPayment = parseFloat(formData.payment_plan.down_payment) || 0;
    const totalFinanced = total - downPayment;
    const apr = parseFloat(formData.payment_plan.apr) || 0;
    const months = parseInt(formData.payment_plan.number_of_months) || 1;
    
    let monthlyPayment;
    if (apr === 0) {
      monthlyPayment = totalFinanced / months;
    } else {
      const monthlyRate = apr / 100 / 12;
      monthlyPayment = totalFinanced * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    }

    setFormData({
      ...formData,
      payment_plan: {
        ...formData.payment_plan,
        monthly_payment: monthlyPayment,
        total_financed: totalFinanced
      }
    });
  };

  const calculateLeasePlan = () => {
    if (!formData.lease_plan.enabled) return;

    const subtotal = formData.items.reduce((sum, item) => sum + item.total, 0);
    
    // Calculate discount
    let discountAmount = 0;
    if (formData.discount_type === "percentage") {
      discountAmount = subtotal * (formData.discount_value / 100);
    } else if (formData.discount_type === "fixed") {
      discountAmount = formData.discount_value;
    }
    
    const subtotalAfterDiscount = subtotal - discountAmount;
    const tax = subtotalAfterDiscount * (formData.tax_rate / 100);
    const total = subtotalAfterDiscount + tax;
    
    const downPaymentMonths = parseInt(formData.lease_plan.down_payment_months) || 3;
    const totalMonths = parseInt(formData.lease_plan.total_months) || 12;
    const apr = parseFloat(formData.lease_plan.apr) || 0;
    
    // Calculate monthly payment based on total lease term
    let monthlyPayment = 0;
    if (totalMonths > 0 && total > 0) {
      if (apr > 0) {
        // Calculate with APR using loan amortization formula
        // First, calculate down payment amount (we need to figure out what monthly payment makes sense)
        // We'll use iterative approach: monthly payment is same for all months including down payment period
        // M = P * [r(1+r)^n] / [(1+r)^n - 1] where P is total, n is total months
        const monthlyRate = apr / 100 / 12;
        const n = totalMonths;
        monthlyPayment = total * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
      } else {
        // Simple calculation without interest: divide total by total months
        monthlyPayment = total / totalMonths;
      }
    }
    
    const downPaymentAmount = monthlyPayment * downPaymentMonths;
    
    // Calculate billing start date (lease start + down payment months)
    const leaseStart = new Date();
    const billingStart = new Date(leaseStart);
    billingStart.setMonth(billingStart.getMonth() + downPaymentMonths);
    
    setFormData({
      ...formData,
      lease_plan: {
        ...formData.lease_plan,
        monthly_payment: monthlyPayment,
        down_payment_amount: downPaymentAmount,
        billing_start_date: billingStart.toISOString(),
        lease_start_date: leaseStart.toISOString()
      }
    });
  };

  useEffect(() => {
    if (formData.payment_plan.enabled) {
      calculatePaymentPlan();
    }
  }, [
    formData.items,
    formData.discount_type,
    formData.discount_value,
    formData.tax_rate,
    formData.payment_plan.enabled,
    formData.payment_plan.down_payment,
    formData.payment_plan.number_of_months,
    formData.payment_plan.apr
  ]);

  useEffect(() => {
    if (formData.lease_plan.enabled) {
      calculateLeasePlan();
    }
  }, [
    formData.items,
    formData.discount_type,
    formData.discount_value,
    formData.tax_rate,
    formData.lease_plan.enabled,
    formData.lease_plan.total_months,
    formData.lease_plan.down_payment_months,
    formData.lease_plan.apr
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.items.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    try {
      if (isEdit) {
        await axios.put(`${API}/invoices/${id}`, formData);
        toast.success("Invoice updated successfully");
      } else {
        await axios.post(`${API}/invoices`, formData);
        toast.success("Invoice created successfully");
      }
      navigate('/invoices');
    } catch (error) {
      toast.error("Error saving invoice");
    }
  };

  const subtotal = formData.items.reduce((sum, item) => sum + item.total, 0);
  
  // Calculate discount
  let discountAmount = 0;
  if (formData.discount_type === "percentage") {
    discountAmount = subtotal * (formData.discount_value / 100);
  } else if (formData.discount_type === "fixed") {
    discountAmount = formData.discount_value;
  }
  
  const subtotalAfterDiscount = subtotal - discountAmount;
  const taxAmount = subtotalAfterDiscount * (formData.tax_rate / 100);
  const total = subtotalAfterDiscount + taxAmount;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" data-testid="invoice-form-title">
          {isEdit ? "Edit Invoice" : "Create Invoice"}
        </h1>
        <Button variant="outline" onClick={() => navigate('/invoices')} data-testid="back-btn">
          Back
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Customer Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Select Customer</Label>
              <Select
                value={formData.customer_id}
                onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                required
              >
                <SelectTrigger data-testid="customer-select">
                  <SelectValue placeholder="Select a customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id} data-testid={`customer-option-${customer.id}`}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sales Agent Name</Label>
              <Input
                type="text"
                value={formData.sales_agent}
                onChange={(e) => setFormData({ ...formData, sales_agent: e.target.value })}
                placeholder="Enter sales agent name"
                data-testid="sales-agent-input"
              />
              <p className="text-xs text-gray-500 mt-1">Name of the sales agent who sold this device</p>
            </div>
            <div>
              <Label>Payment Link (Optional)</Label>
              <Input
                type="url"
                value={formData.payment_link}
                onChange={(e) => setFormData({ ...formData, payment_link: e.target.value })}
                placeholder="https://pay.example.com/customer-name"
                data-testid="payment-link-input"
              />
              <p className="text-xs text-gray-500 mt-1">Online payment link to include in email reminders</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Invoice Items</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-5">
                <Label>Product</Label>
                <Select
                  value={currentItem.product_id}
                  onValueChange={(value) => {
                    const product = products.find(p => p.id === value);
                    setCurrentItem({ 
                      ...currentItem, 
                      product_id: value,
                      price: product ? product.price : 0
                    });
                  }}
                >
                  <SelectTrigger data-testid="product-select">
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id} data-testid={`product-option-${product.id}`}>
                        {product.name} - ${product.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Quantity</Label>
                <Input
                  data-testid="quantity-input"
                  type="number"
                  min="1"
                  value={currentItem.quantity}
                  onChange={(e) => setCurrentItem({ ...currentItem, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="col-span-3">
                <Label>Price</Label>
                <Input
                  data-testid="price-input"
                  type="number"
                  step="0.01"
                  value={currentItem.price}
                  onChange={(e) => setCurrentItem({ ...currentItem, price: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="col-span-2 flex items-end">
                <Button type="button" onClick={addItem} className="w-full" data-testid="add-item-btn">
                  Add Item
                </Button>
              </div>
            </div>

            {formData.items.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={index} data-testid={`item-row-${index}`}>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>${item.price.toFixed(2)}</TableCell>
                      <TableCell>${item.total.toFixed(2)}</TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          data-testid={`remove-item-${index}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <div className="flex justify-end space-y-2 flex-col max-w-sm ml-auto">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="font-bold" data-testid="subtotal-display">${subtotal.toFixed(2)}</span>
              </div>
              
              {/* Discount Section */}
              <div className="border-t pt-2">
                <div className="flex items-center gap-2 mb-2">
                  <Label className="w-32">Discount:</Label>
                  <Select
                    value={formData.discount_type}
                    onValueChange={(value) => setFormData({ ...formData, discount_type: value, discount_value: 0 })}
                  >
                    <SelectTrigger className="w-32" data-testid="discount-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.discount_type !== "none" && (
                    <Input
                      data-testid="discount-value-input"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.discount_value}
                      onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                      className="w-24"
                      placeholder={formData.discount_type === "percentage" ? "%" : "$"}
                    />
                  )}
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount Amount:</span>
                    <span data-testid="discount-display">-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-4">
                <Label>Tax Rate (%):</Label>
                <Input
                  data-testid="tax-rate-input"
                  type="number"
                  step="0.01"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                  className="w-24"
                />
              </div>
              <div className="flex justify-between">
                <span>Tax:</span>
                <span data-testid="tax-display">${taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xl font-bold border-t pt-2">
                <span>Total:</span>
                <span data-testid="total-display">${total.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Payment Options</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Payment Type Selection */}
            <div className="space-y-2">
              <Label>Payment Type</Label>
              <div className="flex gap-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="payment_type"
                    value="full_payment"
                    checked={formData.payment_type === "full_payment"}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        payment_type: e.target.value,
                        payment_plan: { ...formData.payment_plan, enabled: false },
                        lease_plan: { ...formData.lease_plan, enabled: false }
                      });
                    }}
                    data-testid="full-payment-radio"
                  />
                  <span>Full Payment</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="payment_type"
                    value="financing"
                    checked={formData.payment_type === "financing"}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        payment_type: e.target.value,
                        payment_plan: { ...formData.payment_plan, enabled: true },
                        lease_plan: { ...formData.lease_plan, enabled: false }
                      });
                    }}
                    data-testid="financing-radio"
                  />
                  <span>Financing</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="payment_type"
                    value="lease_to_own"
                    checked={formData.payment_type === "lease_to_own"}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        payment_type: e.target.value,
                        payment_plan: { ...formData.payment_plan, enabled: false },
                        lease_plan: { ...formData.lease_plan, enabled: true }
                      });
                    }}
                    data-testid="lease-to-own-radio"
                  />
                  <span>Lease-to-Own</span>
                </label>
              </div>
            </div>

            {/* Financing Plan Fields */}
            {formData.payment_type === "financing" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Down Payment ($)</Label>
                  <Input
                    data-testid="down-payment-input"
                    type="number"
                    step="0.01"
                    value={formData.payment_plan.down_payment}
                    onChange={(e) => setFormData({
                      ...formData,
                      payment_plan: { ...formData.payment_plan, down_payment: parseFloat(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div>
                  <Label>Number of Months (1-36)</Label>
                  <Input
                    data-testid="months-input"
                    type="number"
                    min="1"
                    max="36"
                    value={formData.payment_plan.number_of_months}
                    onChange={(e) => setFormData({
                      ...formData,
                      payment_plan: { ...formData.payment_plan, number_of_months: parseInt(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div>
                  <Label>APR (%)</Label>
                  <Input
                    data-testid="apr-input"
                    type="number"
                    step="0.01"
                    value={formData.payment_plan.apr}
                    onChange={(e) => setFormData({
                      ...formData,
                      payment_plan: { ...formData.payment_plan, apr: parseFloat(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div>
                  <Label>Monthly Payment</Label>
                  <Input
                    data-testid="monthly-payment-display"
                    type="text"
                    value={`$${formData.payment_plan.monthly_payment.toFixed(2)}`}
                    disabled
                  />
                </div>
              </div>
            )}

            {/* Lease-to-Own Plan Fields */}
            {formData.payment_type === "lease_to_own" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Total Lease Term (Months)</Label>
                    <Input
                      data-testid="lease-total-months-input"
                      type="number"
                      min="6"
                      max="60"
                      value={formData.lease_plan.total_months}
                      onChange={(e) => setFormData({
                        ...formData,
                        lease_plan: { ...formData.lease_plan, total_months: parseInt(e.target.value) || 12 }
                      })}
                      placeholder="Enter total months"
                    />
                    <p className="text-xs text-gray-500 mt-1">How long should the lease last?</p>
                  </div>
                  <div>
                    <Label>Down Payment Months</Label>
                    <Select
                      value={formData.lease_plan.down_payment_months.toString()}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        lease_plan: { ...formData.lease_plan, down_payment_months: parseInt(value) }
                      })}
                    >
                      <SelectTrigger data-testid="down-payment-months-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 Months</SelectItem>
                        <SelectItem value="4">4 Months</SelectItem>
                        <SelectItem value="5">5 Months</SelectItem>
                        <SelectItem value="6">6 Months</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Down payment period (3-6 months required)</p>
                  </div>
                  <div className="col-span-2">
                    <Label>APR (%) - Optional</Label>
                    <Input
                      data-testid="lease-apr-input"
                      type="number"
                      step="0.01"
                      value={formData.lease_plan.apr}
                      onChange={(e) => setFormData({
                        ...formData,
                        lease_plan: { ...formData.lease_plan, apr: parseFloat(e.target.value) || 0 }
                      })}
                      placeholder="0"
                      className="w-48"
                    />
                    <p className="text-xs text-gray-500 mt-1">Interest rate on the total invoice amount (leave 0 for no interest)</p>
                  </div>
                </div>
                
                {/* Display calculated values */}
                <div className="bg-green-50 p-4 rounded-lg space-y-3 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">📊 Calculated Payment Plan</h4>
                  <div className="flex justify-between items-center py-2 border-b border-green-200">
                    <span className="font-medium">Monthly Payment:</span>
                    <span className="font-bold text-2xl text-green-700" data-testid="lease-monthly-payment-display">
                      ${formData.lease_plan.monthly_payment.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Down Payment Amount:</span>
                    <span className="font-bold text-green-600" data-testid="lease-down-payment-display">
                      ${(formData.lease_plan.monthly_payment * formData.lease_plan.down_payment_months).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">({formData.lease_plan.down_payment_months} months × ${formData.lease_plan.monthly_payment.toFixed(2)}/month)</span>
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="font-medium">Billing Starts After:</span>
                    <span className="font-semibold">{formData.lease_plan.down_payment_months} months</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Remaining Monthly Payments:</span>
                    <span className="font-semibold">{formData.lease_plan.total_months - formData.lease_plan.down_payment_months} payments</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t-2 border-green-300">
                    <span className="font-medium text-lg">Total Amount to be Paid:</span>
                    <span className="font-bold text-xl text-green-700">
                      ${(formData.lease_plan.monthly_payment * formData.lease_plan.total_months).toFixed(2)}
                    </span>
                  </div>
                  {formData.lease_plan.apr > 0 && (
                    <p className="text-xs text-gray-600 italic mt-2">* Includes {formData.lease_plan.apr}% APR interest</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Credit Card Authorization - Show for financing or lease-to-own */}
        {(formData.payment_type === "financing" || formData.payment_type === "lease_to_own") && (
          <Card className="border-2 border-blue-300">
            <CardHeader className="bg-blue-50">
              <CardTitle className="flex items-center gap-2">
                💳 Credit Card Authorization
                <span className="text-sm font-normal text-gray-600">(For automatic monthly payments)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <p className="text-sm text-gray-600 mb-4">
                Complete this section to authorize automatic monthly charges. This information will appear on the printable authorization form.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cardholder Name *</Label>
                  <Input
                    value={formData.cc_authorization.cardholder_name}
                    onChange={(e) => setFormData({
                      ...formData,
                      cc_authorization: { ...formData.cc_authorization, cardholder_name: e.target.value }
                    })}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label>ID Number (Driver's License / Passport) *</Label>
                  <Input
                    value={formData.cc_authorization.id_number}
                    onChange={(e) => setFormData({
                      ...formData,
                      cc_authorization: { ...formData.cc_authorization, id_number: e.target.value }
                    })}
                    placeholder="DL123456"
                  />
                </div>
                <div>
                  <Label>Card Number *</Label>
                  <Input
                    value={formData.cc_authorization.card_number}
                    onChange={(e) => setFormData({
                      ...formData,
                      cc_authorization: { ...formData.cc_authorization, card_number: e.target.value }
                    })}
                    placeholder="1234 5678 9012 3456"
                    maxLength="19"
                  />
                </div>
                <div>
                  <Label>Expiration Date (MM/YY) *</Label>
                  <Input
                    value={formData.cc_authorization.expiration_date}
                    onChange={(e) => setFormData({
                      ...formData,
                      cc_authorization: { ...formData.cc_authorization, expiration_date: e.target.value }
                    })}
                    placeholder="12/25"
                    maxLength="5"
                  />
                </div>
                <div>
                  <Label>CVV *</Label>
                  <Input
                    value={formData.cc_authorization.cvv}
                    onChange={(e) => setFormData({
                      ...formData,
                      cc_authorization: { ...formData.cc_authorization, cvv: e.target.value }
                    })}
                    placeholder="123"
                    maxLength="4"
                    type="text"
                  />
                </div>
                <div>
                  <Label>Billing ZIP Code *</Label>
                  <Input
                    value={formData.cc_authorization.billing_zip}
                    onChange={(e) => setFormData({
                      ...formData,
                      cc_authorization: { ...formData.cc_authorization, billing_zip: e.target.value }
                    })}
                    placeholder="90210"
                  />
                </div>
              </div>
              <div className="bg-yellow-50 p-3 rounded border border-yellow-200 mt-4">
                <p className="text-xs text-yellow-800">
                  ⚠️ This information will be included on the printable invoice for signature authorization.
                  Customer signature and date will be collected on the printed form.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Payment Terms</Label>
              <Input
                data-testid="payment-terms-input"
                value={formData.payment_terms}
                onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                placeholder="e.g., Net 30, Due on Receipt"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                data-testid="notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes or terms"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/invoices')} data-testid="cancel-btn">
            Cancel
          </Button>
          <Button type="submit" data-testid="save-invoice-btn">
            {isEdit ? "Update" : "Create"} Invoice
          </Button>
        </div>
      </form>
    </div>
  );
};

